// ============================================================================
// OBT F3 — Cevap inceleme dosyası (salt-okunur içerik + BM işlemleri)
// ============================================================================
// İnceleyen: immutable snapshot sırasındaki cevaplar, bağlı fotoğraflar,
// revizyon geçmişi ve inceleme geçmişini görür. Formen cevabı DEĞİŞTİRİLMEZ.
// BM işlemleri (başlat/onayla/reddet/revizyon iste) yalnız engineer rolünde;
// MY işlemleri (başlat/onayla/revizyon iste→BM'ye/müdüre gönder) yalnız deputy
// rolünde ve backend'in izin verdiği duruma göre etkinleşir. Müdür SALT-OKUNUR.
// ============================================================================

import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Edit3,
  PlayCircle,
  History,
  MessageSquare,
  Send,
} from 'lucide-react';
import { ObtLoading, ObtErrorState, ObtStatusBadge } from '../ObtStates';
import { readResponsePhoto } from './reviewApi';
import {
  RESPONSE_STATUS_LABELS,
  REVIEW_STATE_LABELS,
  REVISION_EVENT_LABELS,
  REVIEW_ACTION_LABELS,
  reviewStateLabel,
  labelFor,
} from './reviewEnums';
import { useResponseReview } from './hooks/useResponseReview';
import AnswerReviewField from './AnswerReviewField';
import ReviewMediaGallery from './ReviewMediaGallery';
import ParkAssetHistoryCard from '../memory/ParkAssetHistoryCard';
import RevisionRequestDialog from './RevisionRequestDialog';

function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('tr-TR', {
    timeZone: 'Europe/Istanbul',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function InfoRow({ label, children }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
        {label}
      </dt>
      <dd className="text-sm text-gray-900">{children}</dd>
    </div>
  );
}

// review_state → BM'nin hangi işlemleri yapabileceği (backend BM_ACTIONABLE ile
// hizalı; nihai otorite backend, buton yalnız ön-görünürlük).
function bmCapabilities(reviewState) {
  const notStarted = reviewState == null || reviewState === '';
  const inBmReview = reviewState === 'bm_review';
  const bounced = reviewState === 'my_revision_requested';
  return {
    canStart: notStarted,
    canDecide: inBmReview || bounced, // approve/reject/request-revision
  };
}

// review_state → MY'nin hangi işlemleri yapabileceği (backend akışıyla hizalı):
// bm_approved →[Başlat]→ my_review →[Onayla]→ my_approved →[Müdüre Gönder]→
// director_review. my_review'da Revizyon İste cevabı BM kademesine geri gönderir.
function myCapabilities(reviewState) {
  return {
    canStart: reviewState === 'bm_approved',
    canDecide: reviewState === 'my_review', // approve / request-revision (BM'ye)
    canSendToDirector: reviewState === 'my_approved',
  };
}

/**
 * @param {object} props
 * @param {string} props.responseId
 * @param {'engineer'|'deputy'|'director'} props.role
 * @param {string} [props.respondentName]
 * @param {{title?: string, topic?: string}} [props.campaign]
 * @param {() => void} [props.onChanged]
 * @param {() => void} [props.onBack]
 */
export default function ResponseReviewDetail({
  responseId,
  role,
  respondentName,
  campaign,
  onChanged,
  onCompleted,
  onBack,
}) {
  // Terminal işlem (bu cevap artık aktörün kuyruğundan çıkar) sonrası sıradaki
  // cevaba geç: reddet/revizyon/BM-onayla/Müdüre-gönder. `ok` başarıysa çağrılır.
  const completeIf = (ok) => {
    if (ok && onCompleted) onCompleted();
  };
  const {
    loading,
    error,
    detail,
    reload,
    actionError,
    actionPending,
    bmActions,
    myActions,
  } = useResponseReview(responseId, { role, onChanged });

  const [dialog, setDialog] = useState(null); // 'reject' | 'revision' | null

  const isBm = role === 'engineer';
  const isMy = role === 'deputy';
  // MY, cevabı BM'ye geri gönderdiyse (my_revision_requested): BM bu durumda
  // MY'ye YANIT verip cevabı düzeltmeden geri onaylayabilmeli (madde 1).
  const isBounced = isBm && detail?.review_state === 'my_revision_requested';
  // Rol-duyarlı işlem seti ve yetki kapıları (nihai otorite backend'de).
  const actions = isBm ? bmActions : isMy ? myActions : null;
  const caps = useMemo(
    () =>
      isMy
        ? myCapabilities(detail?.review_state)
        : bmCapabilities(detail?.review_state),
    [isMy, detail?.review_state]
  );

  const loadPhoto = useMemo(
    () => (photoId, opts) => readResponsePhoto(responseId, photoId, opts),
    [responseId]
  );

  if (loading) return <ObtLoading label="Cevap dosyası yükleniyor…" />;
  if (error) return <ObtErrorState error={error} onRetry={reload} />;
  if (!detail) return null;

  const showActions = Boolean(actions);
  // Bounced ise MY'nin son geri-gönderme gerekçesi (BM'nin yanıtlayacağı soru).
  const myBounceComment = isBounced
    ? [...(detail.review_actions || [])]
        .reverse()
        .find((a) => a.action === 'my_revision_requested')?.comment
    : null;

  return (
    <div className="space-y-4">
      {isBounced && (
        <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900 ring-1 ring-inset ring-amber-200">
          <p className="font-semibold">Müdür Yardımcısı bu cevabı size geri gönderdi.</p>
          {myBounceComment && (
            <p className="mt-0.5">MY’nin talebi: “{myBounceComment}”</p>
          )}
          <p className="mt-0.5 text-xs">
            Yanıtınızı yazıp <strong>“MY’ye Yanıtla ve Onayla”</strong> ile geri
            onaylayabilir ya da Formene revizyon gönderebilirsiniz.
          </p>
        </div>
      )}
      <div className="flex items-center justify-between gap-2">
        {onBack && (
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" /> Geri
          </Button>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <ObtStatusBadge value={detail.status} labelMap={RESPONSE_STATUS_LABELS} />
          <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-700 ring-1 ring-inset ring-slate-300">
            {reviewStateLabel(detail.review_state)}
          </span>
        </div>
      </div>

      {/* Üst bilgi */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Cevap Bilgileri</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <InfoRow label="Bilgi Talebi">{campaign?.title || '—'}</InfoRow>
            <InfoRow label="Park">{detail.park_name || '—'}</InfoRow>
            <InfoRow label="Formen">
              {respondentName || 'Bilinmeyen personel'}
            </InfoRow>
            <InfoRow label="Gönderim">{formatDateTime(detail.submitted_at)}</InfoRow>
            <InfoRow label="Revizyon sayısı">{detail.revision_count ?? 0}</InfoRow>
          </dl>
          {detail.response_text && (
            <div className="mt-3 rounded-md bg-gray-50 p-3">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Formen'in bu park hakkındaki genel notu
              </p>
              <p className="mt-1 whitespace-pre-wrap break-words text-sm text-gray-700">
                {detail.response_text}
              </p>
              <p className="mt-1 text-xs text-gray-400">
                (İnceleyene yazılan mesajlar aşağıdaki “Gönderim / Revizyon Geçmişi”nde
                görünür.)
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cevaplar */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Sorular ve Cevaplar</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {detail.answers.length === 0 ? (
            <p className="text-sm text-gray-500">Bu cevapta yanıt bulunmuyor.</p>
          ) : (
            detail.answers.map((a, i) => (
              <AnswerReviewField key={String(a.question_id)} answer={a} index={i} />
            ))
          )}
        </CardContent>
      </Card>

      {/* Medya */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Fotoğraflar</CardTitle>
        </CardHeader>
        <CardContent>
          <ReviewMediaGallery photoIds={detail.photo_ids} loadPhoto={loadPhoto} />
        </CardContent>
      </Card>

      {/* F3-D: Parkın kurumsal hafıza (varlık) geçmişi — salt-okunur bağlam.
          Erişim yoksa kart kendini gizler (inceleme akışı bozulmaz). */}
      {detail.park_id && <ParkAssetHistoryCard parkId={detail.park_id} />}

      {/* Revizyon geçmişi */}
      {detail.revisions.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <History className="h-4 w-4" aria-hidden="true" /> Gönderim / Revizyon Geçmişi
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {detail.revisions.map((r) => (
              <div
                key={r.revision_number}
                className="flex items-start gap-2 border-l-2 border-gray-200 pl-3 text-sm"
              >
                <span className="font-medium text-gray-700">#{r.revision_number}</span>
                <div>
                  <span className="font-medium text-gray-700">
                    {labelFor(REVISION_EVENT_LABELS, r.event)}
                  </span>
                  {r.actor_name && (
                    <span className="text-gray-500"> · {r.actor_name}</span>
                  )}
                  {r.comment && (
                    <p className="mt-0.5 text-gray-700">{r.comment}</p>
                  )}
                  <p className="text-xs text-gray-400">{formatDateTime(r.created_at)}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* İnceleme geçmişi */}
      {detail.review_actions.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <MessageSquare className="h-4 w-4" aria-hidden="true" /> İnceleme Geçmişi
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {detail.review_actions.map((a) => (
              <div
                key={String(a.review_action_id)}
                className="flex flex-wrap items-center gap-2 border-l-2 border-slate-200 pl-3 text-sm"
              >
                <span className="font-semibold text-slate-700">
                  <span className="uppercase text-slate-500">{a.review_level}</span>
                  {a.actor_name ? ` · ${a.actor_name}` : ''}
                </span>
                <span className="text-gray-700">
                  {labelFor(REVIEW_ACTION_LABELS, a.action)}
                </span>
                {a.new_state && (
                  <span className="text-xs text-gray-500">
                    → {reviewStateLabel(a.new_state)}
                  </span>
                )}
                <span className="text-xs text-gray-400">
                  {formatDateTime(a.created_at)}
                </span>
                {a.comment && (
                  <p className="w-full text-gray-700">{a.comment}</p>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* İnceleme işlemleri (rol-duyarlı: BM veya MY) */}
      {showActions && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">İnceleme İşlemleri</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {actionError && (
              <p role="alert" className="text-sm text-red-600">
                {actionError.message}
              </p>
            )}
            {caps.canStart && (
              <Button onClick={() => actions.start()} disabled={actionPending}>
                <PlayCircle className="mr-2 h-4 w-4" aria-hidden="true" /> İncelemeyi Başlat
              </Button>
            )}
            {caps.canDecide && (
              <>
                <Separator />
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={async () => {
                      // BM, MY'nin geri gönderdiği cevabı onaylıyorsa MY'ye YANIT
                      // yazsın (madde 1) → diyalog aç. Diğer hâllerde doğrudan onayla.
                      if (isBounced) {
                        setDialog('approveReply');
                        return;
                      }
                      const ok = await actions.approve();
                      // BM onayı cevabı MY'ye taşır → terminaldir (sıradakine geç).
                      // MY onayı ise ara adım (ardından "Müdüre Gönder") → kalır.
                      if (isBm) completeIf(ok);
                    }}
                    disabled={actionPending}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle2 className="mr-2 h-4 w-4" aria-hidden="true" />
                    {isBounced ? "MY'ye Yanıtla ve Onayla" : 'Onayla'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setDialog('revision')}
                    disabled={actionPending}
                  >
                    <Edit3 className="mr-2 h-4 w-4" aria-hidden="true" /> Revizyon İste
                  </Button>
                  {isBm && (
                    <Button
                      variant="outline"
                      className="border-red-300 text-red-700 hover:bg-red-50"
                      onClick={() => setDialog('reject')}
                      disabled={actionPending}
                    >
                      <XCircle className="mr-2 h-4 w-4" aria-hidden="true" /> Reddet
                    </Button>
                  )}
                </div>
              </>
            )}
            {isMy && caps.canSendToDirector && (
              <>
                <Separator />
                <p className="text-xs text-gray-500">
                  Cevap onaylandı. İncelemenizi Müdür'ün nihai kararına iletebilirsiniz.
                </p>
                <Button
                  onClick={async () => {
                    const ok = await actions.sendToDirector();
                    completeIf(ok); // terminal → sıradaki cevaba geç
                  }}
                  disabled={actionPending}
                >
                  <Send className="mr-2 h-4 w-4" aria-hidden="true" /> Müdüre Gönder
                </Button>
              </>
            )}
            {!caps.canStart &&
              !caps.canDecide &&
              !(isMy && caps.canSendToDirector) && (
                <p className="text-sm text-gray-500">
                  Bu cevap mevcut durumunda sizin işleminizi beklemiyor.
                </p>
              )}
          </CardContent>
        </Card>
      )}

      {showActions && (
        <>
          {/* Madde 1 — BM, MY'nin geri gönderdiği cevabı MY'ye YANITLA ve onayla. */}
          {isBm && (
            <RevisionRequestDialog
              open={dialog === 'approveReply'}
              onOpenChange={(v) => setDialog(v ? 'approveReply' : null)}
              onConfirm={async (comment) => {
                const ok = await bmActions.approve(comment);
                setDialog(null);
                completeIf(ok); // terminal → sıradaki cevaba geç
              }}
              title="MY'ye yanıtla ve onayla"
              description="Müdür Yardımcısının talebine yanıtınızı yazın; cevap düzeltilmeden MY kademesine geri onaylanır. Yanıt inceleme geçmişinde görünür."
              confirmLabel="Yanıtla ve Onayla"
              commentLabel="MY'ye yanıtınız"
              placeholder="Ör. su kaçağı havuzun kuzey kenarındadır…"
              error={actionError?.message || null}
            />
          )}
          <RevisionRequestDialog
            open={dialog === 'revision'}
            onOpenChange={(v) => setDialog(v ? 'revision' : null)}
            onConfirm={async (comment) => {
              const ok = await actions.requestRevision(comment);
              setDialog(null);
              completeIf(ok); // terminal → sıradaki cevaba geç
            }}
            title="Revizyon İste"
            description={
              isMy
                ? 'Bu cevap düzeltme için Bölge Mühendisi kademesine geri gönderilecektir.'
                : 'Bu cevap Formene düzeltme için geri gönderilecektir.'
            }
            confirmLabel="Revizyon İste"
            error={actionError?.message || null}
          />
          {isBm && (
            <RevisionRequestDialog
              open={dialog === 'reject'}
              onOpenChange={(v) => setDialog(v ? 'reject' : null)}
              onConfirm={async (comment) => {
                const ok = await bmActions.reject(comment);
                setDialog(null);
                completeIf(ok); // terminal → sıradaki cevaba geç
              }}
              title="Cevabı Reddet"
              description="Bu cevap reddedilecek ve inceleme kapanacaktır."
              confirmLabel="Reddet"
              commentLabel="Ret gerekçesi"
              placeholder="Ret gerekçesini yazın…"
              error={actionError?.message || null}
            />
          )}
        </>
      )}
    </div>
  );
}
